import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Info } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { NUMBER } from '../../constant/Number.constant';
import { useCompany } from '../../hooks/useCompany';
import { useUpdateBulkChargersMutation } from '../../services/charger.api';
import { useFetchPricingModelsQuery } from '../../services/pricing.api';
import { Charger } from '../../stores/types';
import {
  ChargerSharedDetails,
  Connectivity,
  TRAILER_ACCESSIBILITY,
} from '../../stores/types/chargers.interface';
import { transformTrailerAccessibilityToString } from './charger-detail/utils';
import { Price, PricingDropdown } from '../../stores/types/price.interface';
import { formatDate } from '../../utils/Date.Util';
import {
  Button,
  ButtonSize,
  ButtonType,
  CheckBox,
  CheckBoxData,
  ColorType,
  DateTimePicker,
  Dropdown,
  FormInput,
  GroupDirection,
  Icon,
  Label,
  LabelType,
  Pill,
  RadioGroup,
  Switch,
} from '../_ui';
import { Form } from '../_ui/Form.component';
import { InputType, ValidationType } from '../_ui/Input.component';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import { CustomInputType } from '../_ui/date-picker/Date-Picker.component';

const CONNECTION_TYPE_OPTIONS = [
  { id: Connectivity.SIM_CARD, labelKey: 'chargers_sim_card' },
  { id: Connectivity.WIFI, labelKey: 'chargers_wifi' },
  { id: Connectivity.ETHERNET, labelKey: 'chargers_ethernet' },
];

// Returns the value every charger has in common, or undefined when they differ
const getSharedValue = <T,>(chargers: Charger[], getValue: (charger: Charger) => T | undefined) => {
  const first = getValue(chargers[0]);
  return chargers.every((charger) => getValue(charger) === first) ? first : undefined;
};

interface ChargerBulkEditorProps {
  selectedChargers?: Charger[];
  hideModal: () => void;
  setSelectedChargers: (chargers: Charger[]) => void;
}

export const ChargerBulkEditor = ({
  selectedChargers = [],
  hideModal,
  setSelectedChargers,
}: ChargerBulkEditorProps) => {
  const { t } = useTranslation();
  const { companyId } = useCompany();
  const [bulkChargersDetails, setBulkChargersDetails] = useState<ChargerSharedDetails>({
    deploymentStatus: false,
    hiddenOnMap: false,
    priceModelId: '',
  } as ChargerSharedDetails);

  const { userScope } = useCompany();

  const [showHide, setShowHide] = useState(false);
  const [updateBulkChargers, updateBulkChargerQueryResult] = useUpdateBulkChargersMutation();
  // this will become true if users edit deployment status or deployment date
  const [shouldUpdateDeployedStatus, setShouldUpdateDeployedStatus] = useState(false);
  const [shouldUpdateMaintenance, setShouldUpdateMaintenance] = useState(false);
  const [maintenanceFlag, setMaintenanceFlag] = useState(false);
  const [shouldUpdateAutoStart, setShouldUpdateAutoStart] = useState(false);
  const [autoStartEnabled, setAutoStartEnabled] = useState(false);
  const [leftTrailer, setLeftTrailer] = useState('');
  const [rightTrailer, setRightTrailer] = useState('');
  // undefined means the user has not touched the field, so it shows the chargers' shared value
  const [connectionTypeEdit, setConnectionTypeEdit] = useState<Connectivity>();

  const sharedConnectionType = useMemo(
    () =>
      getSharedValue(selectedChargers, (item: Charger) => item.deploymentInfo?.connectivityType),
    [selectedChargers],
  );
  const connectionTypesDiffer = useMemo(
    () =>
      new Set(selectedChargers.map((item: Charger) => item.deploymentInfo?.connectivityType)).size >
      1,
    [selectedChargers],
  );
  const isMaintenanceMixed = useMemo(
    () =>
      !selectedChargers.every(
        (item: Charger) => item.maintenanceFlag === selectedChargers[0].maintenanceFlag,
      ),
    [selectedChargers],
  );
  const isAutoStartMixed = useMemo(
    () =>
      !selectedChargers.every(
        (item: Charger) => item.autoStartEnabled === selectedChargers[0].autoStartEnabled,
      ),
    [selectedChargers],
  );

  const connectionType = connectionTypeEdit ?? sharedConnectionType;

  const connectionTypeItems = CONNECTION_TYPE_OPTIONS.map((option) => ({
    id: option.id,
    label: t(option.labelKey),
    selected: connectionType === option.id,
  }));

  const isPricingRuleSame = useMemo(() => {
    return selectedChargers.every(
      (item: Charger) => item.priceModelId === selectedChargers[0].priceModelId,
    );
  }, [selectedChargers]);

  // Checking if selected charger(s) have same deployment status
  const isDeploymentStatusSame = useMemo(() => {
    return selectedChargers.every(
      (item: Charger) =>
        item.deploymentInfo?.deployed === selectedChargers[0].deploymentInfo?.deployed,
    );
  }, [selectedChargers]);

  const checkHiddenOnMapStatus = (chargers: Charger[]) => {
    const allHidden = chargers.every((item: Charger) => item.hiddenOnMap);
    const allVisible = chargers.every((item: Charger) => !item.hiddenOnMap);

    if (allHidden) return true;
    if (allVisible) return false;
    return 'indeterminate';
  };

  useEffect(() => {
    if (!selectedChargers || selectedChargers.length === 0) return;

    setBulkChargersDetails((prev) => {
      let updated = { ...prev };

      // Setting charger visibility
      if (selectedChargers.length === 1) {
        updated = {
          ...updated,
          hiddenOnMap: selectedChargers[0].hiddenOnMap,
        };
      } else {
        const result = checkHiddenOnMapStatus(selectedChargers);
        updated = {
          ...updated,
          hiddenOnMap: result,
        };
      }

      // Setting priceModelId if all pricing rules are same
      if (isPricingRuleSame) {
        updated = {
          ...updated,
          priceModelId: selectedChargers[0].priceModelId,
        };
      }

      updated = {
        ...updated,
        deploymentStatus:
          selectedChargers[0].deploymentInfo && isDeploymentStatusSame
            ? selectedChargers[0].deploymentInfo.deployed
            : false,
      };

      return updated;
    });

    const maintenanceSame = selectedChargers.every(
      (item: Charger) => item.maintenanceFlag === selectedChargers[0].maintenanceFlag,
    );
    setMaintenanceFlag(maintenanceSame ? Boolean(selectedChargers[0].maintenanceFlag) : false);
    const autoStartSame = selectedChargers.every(
      (item: Charger) => item.autoStartEnabled === selectedChargers[0].autoStartEnabled,
    );
    setAutoStartEnabled(autoStartSame ? Boolean(selectedChargers[0].autoStartEnabled) : false);
  }, [selectedChargers, isPricingRuleSame, isDeploymentStatusSame]);

  const { pricing } = useFetchPricingModelsQuery(
    { companyId: companyId!, scope: userScope! },
    {
      selectFromResult: (endPoint: any) => ({
        pricing: endPoint.data ? endPoint.data : [],
      }),
    },
  );

  const pricingDropdown = useMemo(() => {
    return pricing.map((pricingObj: Price) => ({
      id: pricingObj.id,
      label: pricingObj.displayName,
      selected: isPricingRuleSame ? pricingObj.id === selectedChargers[0].priceModelId : '',
    }));
  }, [pricing, selectedChargers, isPricingRuleSame]);

  const handleInputChange = (key: string, value: string) => {
    setBulkChargersDetails({ ...bulkChargersDetails, [key]: value });
  };

  const handleToggleChange = (key: string, value: boolean) => {
    setBulkChargersDetails({
      ...bulkChargersDetails,
      [key]: value,
    });

    // we should send deployment status in request
    if (key === 'deploymentStatus') {
      setShouldUpdateDeployedStatus(true);
    }
  };

  // Removed button validation based on comments on BF-1226
  // const onButtonValidation = () => {
  //   if (
  //     bulkChargersDetails.deploymentStatus &&
  //     !bulkChargersDetails.deploymentDate
  //   ) {
  //     return false;
  //   }
  //   return true;
  // };

  const handleEditBulkChargers = () => {
    const payload = selectedChargers.map((ele: Charger) => {
      let result: any = {
        deviceId: ele.id,
        chargerModelId: ele.chargerModelId,
        hiddenOnMap:
          bulkChargersDetails.hiddenOnMap === 'indeterminate'
            ? ele.hiddenOnMap
            : bulkChargersDetails.hiddenOnMap,
        directions: bulkChargersDetails.directions,
        internalNote: bulkChargersDetails.internalNote,
        installer: bulkChargersDetails.installer,
        installationDate: bulkChargersDetails.installationDate,
        installerContactEmail: bulkChargersDetails.installerContactEmail,
        displayId: ele.name,
      };

      if (bulkChargersDetails.priceModelId !== '') {
        result = { ...result, priceModelId: bulkChargersDetails.priceModelId };
      }

      if (shouldUpdateDeployedStatus) {
        result = {
          ...result,
          deploymentStatus: bulkChargersDetails.deploymentStatus,
        };
      }
      if (shouldUpdateMaintenance) {
        result.maintenanceFlag = maintenanceFlag;
      }
      if (shouldUpdateAutoStart) {
        result.autoStartConfig = { type: autoStartEnabled ? 'SIMULATED' : 'DISABLED' };
      }
      if (leftTrailer || rightTrailer) {
        result.trailerAccessibilityByLocation = {
          ...(leftTrailer && { LEFT: leftTrailer }),
          ...(rightTrailer && { RIGHT: rightTrailer }),
        };
      }

      return result;
    });
    updateBulkChargers({
      bulkChargers: payload,
      bulkOperation: payload.length > 1,
      autoPopulate: true,
    });
    setSelectedChargers([]);
  };

  const selectedChargersCount = selectedChargers.length;

  const countOfChargerToDisplay = showHide ? selectedChargersCount : NUMBER.TEN;

  const mapVisibilityLabel = () => (
    <Label
      text={t('chargers_map_visibility_hint')}
      type={LabelType.BODY3}
      color={ColorType.WHITE}
    />
  );

  return (
    <Form
      onSubmit={handleEditBulkChargers}
      queryResult={updateBulkChargerQueryResult}
      onQuerySuccess={() => {
        hideModal();
      }}
      onQueryFailed={() => {
        hideModal();
      }}
    >
      <div className='divide-y divide-grey2'>
        <div className='pb-4'>
          <div className='flex items-baseline'>
            <Label
              className='pb-4'
              text={t('setting_applied')}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
            <Label
              className='pb-4 pl-1'
              text={t('setting_applied_to_all_chargers')}
              type={LabelType.LABEL_S}
              color={ColorType.BLACK}
            />
          </div>
          <div className='flex-wrap' style={{ display: 'flex' }}>
            {selectedChargers.slice(0, countOfChargerToDisplay).map((ele: Charger) => (
              <div className='pr-2 pb-2' key={ele.id}>
                <Pill label={ele.name} labelColor={ColorType.GREY6} />
              </div>
            ))}
          </div>
          {selectedChargersCount > NUMBER.TEN && (
            <div
              className='text-sm font-medium whitespace-pre text-brand2 cursor-pointer'
              onClick={() => setShowHide(!showHide)}
            >
              {`View ${showHide ? 'less' : 'more'} (showing ${
                !showHide ? NUMBER.TEN : selectedChargersCount
              } of ${selectedChargersCount})`}
            </div>
          )}
        </div>
        <div className='pt-4'>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-y-2'>
              <Label
                text={t('chargers_pricing_rule')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
              />
              <Dropdown
                headerWidth='500px'
                items={pricingDropdown}
                placeholder={t('select_pricing_rule')}
                onItemClick={(selectedItem: PricingDropdown) =>
                  handleInputChange('priceModelId', selectedItem.id)
                }
                labelType={LabelType.BODY3_G4}
                contentDivWidth={500}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <div className='flex flex-row gap-1 items-center'>
                <Label
                  text={t('chargers_map_visibility')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                />
                <Tooltip>
                  <TooltipTrigger>
                    <Icon src={Info} size={IconSize.SIZE_20x20} />
                  </TooltipTrigger>
                  <TooltipContent>{mapVisibilityLabel()}</TooltipContent>
                </Tooltip>
              </div>
              <CheckBox
                label={t('chargers_visibility_user_access')}
                key='visible'
                index={0}
                selected={!bulkChargersDetails.hiddenOnMap}
                onChange={(value: boolean) => handleToggleChange('hiddenOnMap', !value)}
                indeterminate={bulkChargersDetails.hiddenOnMap === 'indeterminate'}
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <div className='flex flex-row gap-1 items-center'>
                <Label
                  text={t('charger_auto_start')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                />
                <Tooltip>
                  <TooltipTrigger>
                    <Icon src={Info} size={IconSize.SIZE_20x20} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Trans i18nKey='chargers_auto_start_hint' components={{ br: <br /> }} />
                  </TooltipContent>
                </Tooltip>
              </div>
              <Switch
                value={autoStartEnabled}
                enableLabel={t('enabled')}
                disableLabel={
                  isAutoStartMixed && !shouldUpdateAutoStart
                    ? t('edit_bulk_charger_some_enabled')
                    : t('disabled')
                }
                onChange={(value: boolean) => {
                  setShouldUpdateAutoStart(true);
                  setAutoStartEnabled(value);
                }}
                dataTestId='bulk-autostart-switch'
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <div className='flex flex-row gap-1 items-center'>
                <Label
                  text={t('maintenance_flag')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                />
                <Tooltip>
                  <TooltipTrigger>
                    <Icon src={Info} size={IconSize.SIZE_20x20} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Trans i18nKey='maintenance_flag_tip' components={{ br: <br /> }} />
                  </TooltipContent>
                </Tooltip>
              </div>
              <Switch
                value={maintenanceFlag}
                enableLabel={t('under_maintenance')}
                disableLabel={
                  isMaintenanceMixed && !shouldUpdateMaintenance
                    ? t('edit_bulk_charger_some_under_maintenance')
                    : t('not_under_maintenance')
                }
                onChange={(value: boolean) => {
                  setShouldUpdateMaintenance(true);
                  setMaintenanceFlag(value);
                }}
                dataTestId='bulk-maintenance-switch'
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <Label
                text={t('chargers_connection_type')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
              />
              <RadioGroup
                direction={GroupDirection.Horizontal}
                defaultItems={connectionTypeItems}
                onChange={(items: CheckBoxData[]) =>
                  setConnectionTypeEdit(
                    items.find((item) => item.selected)?.id as Connectivity | undefined,
                  )
                }
                dataTestId='charger-edit-connection-type'
                itemHeight='24px'
              />
              {connectionTypesDiffer && connectionTypeEdit === undefined && (
                <Label
                  text={t('edit_bulk_charger_connection_types_differ')}
                  type={LabelType.BODY3}
                  color={ColorType.GREY6}
                />
              )}
            </div>
            <div className='flex flex-col gap-y-2'>
              <FormInput
                defaultValue={bulkChargersDetails?.directions}
                label={t('chargers_directions')}
                secondLabel={`(${t('chargers_optional')})`}
                secondLabelType={LabelType.LABEL_M_MEDIUM}
                secondLabelColor={ColorType.GREY6}
                placeholder={t('chargers_directions')}
                inputType={InputType.TEXT}
                props={{ style: { width: '500px' } }}
                onChange={(event) => handleInputChange('directions', event.target.value)}
              />
            </div>
            <div className='flex flex-col gap-y-2'>
              <FormInput
                defaultValue={bulkChargersDetails?.internalNote}
                label={t('chargers_internal_note')}
                secondLabel={`(${t('chargers_optional')})`}
                secondLabelType={LabelType.LABEL_M_MEDIUM}
                secondLabelColor={ColorType.GREY6}
                placeholder={t('chargers_internal_note_placeholder')}
                inputType={InputType.TEXT}
                props={{ style: { width: '500px' } }}
                onChange={(event) => handleInputChange('internalNote', event.target.value)}
              />
            </div>
            <div className='flex flex-row gap-4 justify-between'>
              <div className='flex flex-col gap-y-2 w-full'>
                <FormInput
                  defaultValue={bulkChargersDetails?.installer}
                  width='100%'
                  label={t('chargers_installer')}
                  secondLabel={`(${t('chargers_optional')})`}
                  secondLabelType={LabelType.LABEL_M_MEDIUM}
                  secondLabelColor={ColorType.GREY6}
                  placeholder={t('chargers_installer')}
                  inputType={InputType.TEXT}
                  onChange={(event) => handleInputChange('installer', event.target.value)}
                />
              </div>
              <div className='flex flex-col gap-y-2'>
                <div className='flex flex-row gap-1'>
                  <Label
                    text={t('chargers_installation_date')}
                    type={LabelType.LABEL_M}
                    color={ColorType.BLACK}
                  />
                  <Label
                    text={`(${t('chargers_optional')})`}
                    type={LabelType.LABEL_M_MEDIUM}
                    color={ColorType.GREY6}
                  />
                </div>
                <DateTimePicker
                  onChange={(date) =>
                    handleInputChange('installationDate', formatDate(date, 'yyyy-MM-dd'))
                  }
                  format='MM/dd/yyyy'
                  customInputType={CustomInputType.FORMINPUTDATE}
                  showDefaultDate={false}
                  customFormInputDivWidth='w-[240px]'
                  customFormInputErrorLabel={t('chargers_date_validation_message')}
                />
              </div>
            </div>
            <div className='flex flex-col gap-y-2'>
              <FormInput
                defaultValue={bulkChargersDetails?.installerContactEmail}
                label={t('edit_bulk_charger_installer_email')}
                secondLabel={`(${t('chargers_optional')})`}
                secondLabelType={LabelType.LABEL_M_MEDIUM}
                secondLabelColor={ColorType.GREY6}
                placeholder={t('chargers_installer_contact_placeholder')}
                inputType={InputType.TEXT}
                validationType={ValidationType.EMAIL}
                showErrorIcon
                errorLabel={t('chargers_email_validation_message')}
                props={{ style: { width: '500px' } }}
                onChange={(event) => handleInputChange('installerContactEmail', event.target.value)}
              />
            </div>
            <div className='flex flex-row gap-4 justify-between'>
              {(['LEFT', 'RIGHT'] as const).map((side) => {
                const value = side === 'LEFT' ? leftTrailer : rightTrailer;
                const setValue = side === 'LEFT' ? setLeftTrailer : setRightTrailer;
                return (
                  <div className='flex flex-col gap-y-2 w-full' key={side}>
                    <Label
                      text={`${t('trailers')} (${t(side === 'LEFT' ? 'left_port' : 'right_port')})`}
                      type={LabelType.LABEL_M}
                      color={ColorType.BLACK}
                    />
                    <Dropdown
                      headerWidth='100%'
                      items={Object.values(TRAILER_ACCESSIBILITY).map((item) => ({
                        id: item,
                        label: t(transformTrailerAccessibilityToString(item)),
                        selected: value === item,
                      }))}
                      placeholder={t('no_change')}
                      onItemClick={(selectedItem: { id: string }) => setValue(selectedItem.id)}
                      labelType={LabelType.BODY3_G4}
                      labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                      chevdownIconHighlightColor={ColorType.GREY6}
                      headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px]'
                      placement='top-start'
                    />
                  </div>
                );
              })}
            </div>
            <div className='flex flex-col pt-[16px] pb-[20px] sticky bottom-0 bg-white'>
              <Button
                label={t('chargers_add_save')}
                type={ButtonType.PRIMARY}
                size={ButtonSize.SMALL_FULL}
                isSumbit
                dataTestId='charger-edit-save'
              />
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};
